/**
 * SEO Utility Functions for KeyQuest
 * Helper functions for SEO operations, URL generation, and content optimization
 */

import { SITE_CONFIG } from '../config/seoConfig';

/**
 * Today's date formatted for use in SEO titles (e.g. "August 5, 2026").
 * Computed at render time, so pages using it get a fresh title on every
 * visit/crawl rather than a date baked in at build time.
 * @returns {string}
 */
export const getSEODateLabel = () => {
    const date = new Date();
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

/**
 * Replace a "{DATE}" placeholder in an SEO title/description with today's
 * date (see getSEODateLabel). Leaves the string untouched if there's no
 * placeholder or the input is falsy.
 * @param {string} text
 * @returns {string}
 */
export const applySEODate = (text) => {
    if (!text) return text;
    return text.replace(/\{DATE\}/g, getSEODateLabel());
};

/**
 * Generate canonical URL for a page
 * @param {string} path - The path of the page (e.g., '/insights/top-apps')
 * @returns {string} - Full canonical URL
 */
export const generateCanonicalUrl = (path) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${SITE_CONFIG.siteUrl}${cleanPath}`.replace(/\/$/, ''); // Remove trailing slash
};

/**
 * Generate Open Graph image URL
 * @param {string} imagePath - Relative or absolute image path
 * @returns {string} - Full image URL
 */
export const generateOGImage = (imagePath) => {
    if (!imagePath) return `${SITE_CONFIG.siteUrl}${SITE_CONFIG.defaultImage}`;
    if (imagePath.startsWith('http')) return imagePath;
    return `${SITE_CONFIG.siteUrl}${imagePath}`;
};

/**
 * Truncate text to specified length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} - Truncated text
 */
export const truncateText = (text, maxLength = 160) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
};

/**
 * Generate meta description from content
 * @param {string} content - Content to extract description from
 * @param {number} maxLength - Maximum description length
 * @returns {string} - Generated description
 */
export const generateMetaDescription = (content, maxLength = 160) => {
    if (!content) return '';

    // Remove HTML tags
    const cleanContent = content.replace(/<[^>]*>/g, '');

    // Remove extra whitespace
    const normalized = cleanContent.replace(/\s+/g, ' ').trim();

    return truncateText(normalized, maxLength);
};

/**
 * Generate keywords from text
 * @param {string} text - Text to extract keywords from
 * @param {number} maxKeywords - Maximum number of keywords
 * @returns {string} - Comma-separated keywords
 */
export const generateKeywords = (text, maxKeywords = 10) => {
    if (!text) return '';

    // Common stop words to filter out
    const stopWords = new Set([
        'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
        'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'been',
        'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
        'should', 'may', 'might', 'can', 'this', 'that', 'these', 'those'
    ]);

    // Extract words, filter stop words, and get unique keywords
    const words = text
        .toLowerCase()
        .replace(/[^\w\s]/g, '')
        .split(/\s+/)
        .filter(word => word.length > 3 && !stopWords.has(word));

    // Count word frequency
    const wordCount = {};
    words.forEach(word => {
        wordCount[word] = (wordCount[word] || 0) + 1;
    });

    // Sort by frequency and take top keywords
    const topKeywords = Object.entries(wordCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, maxKeywords)
        .map(([word]) => word);

    return topKeywords.join(', ');
};

/**
 * Generate slug from text
 * @param {string} text - Text to convert to slug
 * @returns {string} - URL-friendly slug
 */
export const generateSlug = (text) => {
    if (!text) return '';

    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

/**
 * Generate App Details Link
 *
 * Builds the path from `genre[0]` + `name`, matching the link-building
 * already used by CategoryAllPage.jsx/CategorySuggestions.jsx. Deliberately
 * ignores `app.game_url` - that field comes back from the API unslugified
 * (raw spaces, no encoding, e.g. "/world creation/worlds?id=..."), which
 * previously made this function, the game-detail page's own canonical tag,
 * and its "expected slug" redirect all disagree with the URLs actually used
 * by listing pages and the sitemap.
 * @param {object} app - App object containing name, genre, item_id
 * @returns {string} - Full URL path for the app details page
 */
export const getAppDetailsLink = (app) => {
    if (!app) return '/xr/app/unknown';

    const genreSlug = Array.isArray(app.genre) && app.genre.length > 0
        ? generateSlug(app.genre[0])
        : (app.genre && typeof app.genre === 'string' ? generateSlug(app.genre) : 'app');

    const nameSlug = generateSlug(app.name || '') || 'unknown';

    return `/xr/${genreSlug}/${nameSlug}?id=${app.item_id}`;
};

/**
 * Generate breadcrumb data from URL path
 * @param {string} pathname - Current URL pathname
 * @returns {Array} - Breadcrumb items
 */
export const generateBreadcrumbData = (pathname) => {
    const segments = pathname.split('/').filter(Boolean);
    const breadcrumbs = [
        { name: 'Home', path: '/' }
    ];

    let currentPath = '';
    segments.forEach((segment) => {
        currentPath += `/${segment}`;
        const name = segment
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        breadcrumbs.push({ name, path: currentPath });
    });

    return breadcrumbs;
};

/**
 * Check if content meets minimum threshold for indexing
 * @param {number} contentCount - Number of content items
 * @param {number} minThreshold - Minimum threshold
 * @returns {boolean} - Whether content should be indexed
 */
export const shouldIndexContent = (contentCount, minThreshold = 5) => {
    return contentCount >= minThreshold;
};

/**
 * Generate structured data for ItemList (e.g., top apps, trending)
 * @param {string} listName - Name of the list
 * @param {Array} items - Array of items
 * @param {Function} itemMapper - Function to map item to structured data
 * @returns {Object} - ItemList structured data
 */
export const generateItemListStructuredData = (listName, items, itemMapper) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: listName,
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: itemMapper(item),
        })),
    };
};

/**
 * Generate FAQ structured data
 * @param {Array} faqs - Array of {question, answer} objects
 * @returns {Object} - FAQ structured data
 */
export const generateFAQStructuredData = (faqs) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
            },
        })),
    };
};

/**
 * Format date for structured data
 * @param {Date|string|number} date - Date to format
 * @returns {string} - ISO 8601 formatted date
 */
export const formatStructuredDataDate = (date) => {
    if (!date) return new Date().toISOString();
    if (date instanceof Date) return date.toISOString();
    return new Date(date).toISOString();
};

/**
 * Calculate crawl depth from homepage
 * @param {string} pathname - Current URL pathname
 * @returns {number} - Number of clicks from homepage
 */
export const calculateCrawlDepth = (pathname) => {
    if (pathname === '/' || pathname === '') return 0;
    return pathname.split('/').filter(Boolean).length;
};

/**
 * Generate hreflang tags for multi-language support
 * @param {string} pathname - Current URL pathname
 * @param {Array} languages - Array of language codes
 * @returns {Array} - hreflang tag data
 */
export const generateHreflangTags = (pathname, languages = ['en-US']) => {
    const hreflangTags = languages.map(lang => ({
        hreflang: lang,
        href: `${SITE_CONFIG.siteUrl}${pathname}`,
    }));

    // Add x-default for fallback
    hreflangTags.push({
        hreflang: 'x-default',
        href: `${SITE_CONFIG.siteUrl}${pathname}`,
    });

    return hreflangTags;
};

/**
 * Sanitize text for SEO (remove special characters, normalize)
 * @param {string} text - Text to sanitize
 * @returns {string} - Sanitized text
 */
export const sanitizeForSEO = (text) => {
    if (!text) return '';

    return text
        .replace(/[<>]/g, '') // Remove potential HTML
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();
};

/**
 * Generate Twitter Card type based on content
 * @param {boolean} hasImage - Whether content has image
 * @param {boolean} hasVideo - Whether content has video
 * @returns {string} - Twitter card type
 */
export const getTwitterCardType = (hasImage, hasVideo) => {
    if (hasVideo) return 'player';
    if (hasImage) return 'summary_large_image';
    return 'summary';
};

/**
 * Validate and clean URL
 * @param {string} url - URL to validate
 * @returns {string|null} - Cleaned URL or null if invalid
 */
export const validateAndCleanUrl = (url) => {
    if (!url) return null;

    try {
        const urlObj = new URL(url);
        return urlObj.href;
    } catch (e) {
        // If relative URL, prepend site URL
        if (url.startsWith('/')) {
            return `${SITE_CONFIG.siteUrl}${url}`;
        }
        return null;
    }
};

/**
 * Generate robots meta content based on page type and auth status
 * @param {boolean} requiresAuth - Whether page requires authentication
 * @param {boolean} hasContent - Whether page has sufficient content
 * @param {boolean} isDraft - Whether content is draft/unpublished
 * @returns {string} - Robots meta content
 */
export const generateRobotsMetaContent = (requiresAuth = false, hasContent = true, isDraft = false) => {
    if (requiresAuth || isDraft) return 'noindex, nofollow';
    if (!hasContent) return 'noindex, follow';
    return 'index, follow';
};

export default {
    getSEODateLabel,
    applySEODate,
    generateCanonicalUrl,
    generateOGImage,
    truncateText,
    generateMetaDescription,
    generateKeywords,
    generateSlug,
    generateBreadcrumbData,
    shouldIndexContent,
    generateItemListStructuredData,
    generateFAQStructuredData,
    formatStructuredDataDate,
    calculateCrawlDepth,
    generateHreflangTags,
    sanitizeForSEO,
    getTwitterCardType,
    validateAndCleanUrl,
    generateRobotsMetaContent,
    getAppDetailsLink,
};
